import { Box, Button, useMediaQuery } from '@mui/material';
import { ReactComponent as BasicPlanCost } from 'core/assets/svg/pricing-page/basic-plan-cost.svg';
import { ReactComponent as BasicPlanIcon } from 'core/assets/svg/pricing-page/basic-plan.svg';
import { ReactComponent as ElitePlusPlan } from 'core/assets/svg/pricing-page/elite++-plan.svg';
import { ReactComponent as ElitePlanCost } from 'core/assets/svg/pricing-page/elite-plan-cost.svg';
import { ReactComponent as ElitePlanHighlightedIcon } from 'core/assets/svg/pricing-page/elite-plan-highlighted.svg';
import { ReactComponent as ElitePlanIcon } from 'core/assets/svg/pricing-page/elite-plan.svg';
import { ReactComponent as ElitePlusPlanCost } from 'core/assets/svg/pricing-page/enterprise-plan-cost.svg';
import { ReactComponent as FluxIcon } from 'core/assets/svg/pricing-page/flux-icon-pricing.svg';
import { ReactComponent as ImagesIcon } from 'core/assets/svg/pricing-page/image-pricing-icon.svg';
import { ReactComponent as MaxPlanCost } from 'core/assets/svg/pricing-page/max-plan-cost.svg';
import { ReactComponent as MaxPlanIcon } from 'core/assets/svg/pricing-page/max-plan.svg';
import { ReactComponent as NarratorIcon } from 'core/assets/svg/pricing-page/narrator-icon-pricing.svg';
import { ReactComponent as PlusPlanCost } from 'core/assets/svg/pricing-page/plus-plan-cost.svg';
import { ReactComponent as PlusPlanIcon } from 'core/assets/svg/pricing-page/plus-plan.svg';
import { ReactComponent as ProPlanCost } from 'core/assets/svg/pricing-page/pro-plan-cost.svg';
import { ReactComponent as ProPlanIcon } from 'core/assets/svg/pricing-page/pro-plan.svg';
import { ReactComponent as StandardPlanCost } from 'core/assets/svg/pricing-page/standard-plan-cost.svg';
import {
	ReactComponent as StandardPlanIcon,
	ReactComponent as StandardPlanHighLightedIconIcon,
} from 'core/assets/svg/pricing-page/standard-plan.svg';
import { ReactComponent as UltraPlanCost } from 'core/assets/svg/pricing-page/ultra-plan-cost.svg';
import { ReactComponent as VideoAnyoneIcon } from 'core/assets/svg/pricing-page/video-anyone-pricing-icon.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseTypography from 'core/components/typography/BaseTypography';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { PricingPagePlans } from 'core/constants/enums/PricingPagePlans.ts';
import { SubscriptionPlanType } from 'core/constants/enums/SubscriptionPlanType.ts';
import { SubscriptionCardDetails } from 'core/layouts/components/subscription-plans/components/subscription-card/components/SubscriptionCardDetails';
import { subscriptionCardStyles } from 'core/layouts/components/subscription-plans/components/subscription-card/SubscriptionCardStyles';
import appTheme from 'core/layouts/ui-blocks/AppTheme';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import {
	getCollapsedMobilePricingPlansSelector,
	getPayLinkAPIStageSelector,
	getProratedPriceApiStageSelector,
	setOpenedMobilePricingPlans,
} from 'core/store/reducers/profileReducer/ProfileSlice';
import { thousandSeparator } from 'core/utils/numberUtils';
import { formatPrice } from 'core/utils/utils';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function SubscriptionCard({
	id = '',
	plan = {},
	planType = '',
	loading,
	buttonText = '',
	onButtonClick = () => {},
	disableButton = false,
}) {
	const getPayLinkAPIStage = useSelector(getPayLinkAPIStageSelector);
	const getProratedPriceApiStage = useSelector(getProratedPriceApiStageSelector);

	const disableButtonOnOngoingAPICall = useMemo(
		() => getPayLinkAPIStage === ApiStage.LOADING || getProratedPriceApiStage === ApiStage.LOADING,
		[getPayLinkAPIStage, getProratedPriceApiStage],
	);

	const getPerMonthDescription = () => {
		const annualPrice = (plan.monthlyPrice / 100) * 12;
		const formattedAnnualPrice = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(annualPrice);

		if (planType === SubscriptionPlanType.ANNUALLY) {
			return (
				<BaseTypography
					id="per-year-description"
					key="per-year-description"
					variant="tooltip_regular"
					styles={{ color: 'colors.light5', textAlign: 'left' }}
				>
					per month, billed annually, {`${formattedAnnualPrice} per year`}
				</BaseTypography>
			);
		}
		if (planType === SubscriptionPlanType.ONE_TIME) {
			return (
				<BaseTypography
					id="one-time-description"
					key="one-time-description"
					variant="tooltip_regular"
					styles={{ color: 'colors.light3', textAlign: 'left' }}
				>
					one time pack
				</BaseTypography>
			);
		}
		return (
			<BaseTypography
				id="per-month-description"
				key="per-month-description"
				variant="tooltip_regular"
				styles={{ maxWidth: '100%', color: 'colors.light5', textAlign: 'left' }}
			>
				per month, billed monthly
			</BaseTypography>
		);
	};

	const PlanNames = {
		BASIC: 'Basic',
		STANDARD: 'Standard',
		PLUS: 'Plus',
		ELITE: 'Elite',
		FREE: 'Free',
		PRO: 'Pro',
		MAX: 'Max',
		ELITE_PLUS: 'Elite++',
		ULTRA: 'Ultra',
	};
	const plansData = {
		[PlanNames.BASIC]: {
			icon: <BasicPlanIcon />,
			costIcon: <BasicPlanCost />,
		},
		[PlanNames.STANDARD]: {
			icon:
				plan?.displaySettings?.highlightLevel === 1 ? (
					<StandardPlanHighLightedIconIcon />
				) : (
					<StandardPlanIcon />
				),
			costIcon: <StandardPlanCost />,
		},
		[PlanNames.PLUS]: {
			icon: <PlusPlanIcon />,
			costIcon: <PlusPlanCost />,
		},
		[PlanNames.ELITE]: {
			icon:
				plan?.displaySettings?.highlightLevel === 1 ? (
					<ElitePlanHighlightedIcon />
				) : (
					<ElitePlanIcon />
				),
			costIcon: <ElitePlanCost />,
		},
		[PlanNames.PRO]: {
			icon: <ProPlanIcon />,
			costIcon: <ProPlanCost />,
		},
		[PlanNames.MAX]: {
			icon: <MaxPlanIcon />,
			costIcon: <MaxPlanCost />,
		},
		[PlanNames.ELITE_PLUS]: {
			icon: <ElitePlusPlan />,
			costIcon: <ElitePlusPlanCost />,
		},
		[PlanNames.ULTRA]: {
			icon:
				plan?.displaySettings?.highlightLevel === 1 ? (
					<ElitePlanHighlightedIcon />
				) : (
					<ElitePlanIcon />
				),
			costIcon: <UltraPlanCost />,
		},
	};

	const offersDetails = [
		{
			title: 'Priority generations',
			allowed: plan?.features?.priorityGeneration?.restrictions?.allowed,
		},
		{
			title: 'Video Face Swap',
			allowed: plan?.features?.videoFaceSwap?.restrictions?.allowed,
		},
		{
			title: '8K upscale',
			allowed: plan?.features?.upscale?.restrictions?.allowed,
		},
		{
			title: 'True Touch realism enhancer',
			allowed: plan?.features?.trueTouch?.restrictions?.allowed,
		},
		{
			title: `Everything in ${plan.precedingPlan} plan`,
			allowed: plan?.precedingPlan,
		},
		{
			title: 'Commercial use allowed',
			allowed: plan?.features?.commercialUsage?.restrictions?.allowed,
		},
		{
			title: 'API access',
			allowed: plan?.features?.publicApis?.restrictions?.allowed,
		},
		{
			title: 'Expert prompt assist',
			allowed: plan?.features?.expertPromptAssist?.restrictions?.allowed,
		},
		{
			title: 'Remaining credits roll over to next month',
			allowed: plan?.features?.creditsRollOver?.restrictions?.allowed,
		},
		{
			title: `Dedicated ${plan?.features?.dedicatedGpus?.restrictions?.list?.join(' ')} GPUs`,
			allowed: plan?.features?.dedicatedGpus?.restrictions?.allowed,
		},
		{
			title: `${plan?.features?.consulting?.restrictions?.duration}
       ${plan?.features?.consulting?.restrictions?.duration > 1 ? 'hours' : 'hour'} 
       of solution consulting`,
			allowed: plan?.features?.consulting?.restrictions?.allowed,
		},
	];

	const generationsDetails = [
		{
			id: 'flux-details',
			icon: <FluxIcon />,
			text: 'Flux images',
			restrictedText: 'Flux images',
			value: plan?.features?.fluxGeneration?.restrictions?.count,
			name: 'Flux',
		},
		{
			id: 'images-details',
			icon: <ImagesIcon />,
			text: 'SDXL images',
			restrictedText: 'SDXL images',
			value: plan?.features?.modelGeneration?.restrictions?.count,
			name: 'Image Details',
		},
		{
			id: 'video-anyone-details',
			icon: <VideoAnyoneIcon />,
			text: 'seconds of AI video generation ',
			restrictedText: 'AI video generation ',
			value: plan?.features?.videoAnyone?.restrictions?.duration,
			name: 'VideoAnyone',
		},
		{
			id: 'narrator-details',
			icon: <NarratorIcon />,
			text: 'seconds of Narrator Videos',
			restrictedText: 'Narrator Videos',
			value: plan?.features?.narrator?.restrictions?.duration,
			name: 'Narrator',
		},
	];
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(appTheme.breakpoints.down('md'));
	const openedMobilePricingPlansDetails = new Set(
		useSelector(getCollapsedMobilePricingPlansSelector),
	);
	const handleSeeMoreClick = (planName) => {
		const updatedPlans = openedMobilePricingPlansDetails;

		if (openedMobilePricingPlansDetails.has(planName)) {
			updatedPlans.delete(planName);
		} else {
			updatedPlans.add(planName);
		}
		dispatch(setOpenedMobilePricingPlans([...updatedPlans]));
	};
	return (
		<Box
			key={`${id}-wrapper`}
			id={`${id}-wrapper`}
			sx={[subscriptionCardStyles.subscriptionPlanCardWrapper]}
		>
			{loading ? (
				<Box id={id} sx={[subscriptionCardStyles.subscriptionPlanCardCont(false)]}>
					<Box
						id={id}
						sx={[subscriptionCardStyles.subscriptionPlanCard, reusableStyles.skeletonLoaderStyles]}
					/>
				</Box>
			) : (
				<Box
					id={`${id}-cont`}
					sx={[
						subscriptionCardStyles.subscriptionPlanCardCont(
							plan?.displaySettings?.highlightLevel === 1,
							openedMobilePricingPlansDetails,
							plan.name,
							isMobile,
						),
						planType === SubscriptionPlanType.ONE_TIME && { margin: 0 },
					]}
				>
					{plan?.displaySettings?.highlightLevel === 1 && (
						<Box
							sx={[
								reusableStyles.flexRow,
								{
									justifyContent: 'center',
									borderRadius: '8px 8px 0 0',
									marginBottom: '-1px',
									padding: '4px 66px',
								},
							]}
						>
							<BaseTypography
								id="most-bought-text"
								key="most-bought-text"
								variant="t2_medium"
								styles={{ color: 'colors.dark6' }}
							>
								Best Value
							</BaseTypography>
						</Box>
					)}
					<Box
						id={`${id}-card-content`}
						sx={[
							subscriptionCardStyles.subscriptionPlanCardContent,
							plan?.displaySettings?.highlightLevel !== 1
								? { paddingTop: '54px' }
								: { padding: '22px 14px' },
						]}
					>
						<Box
							id={`${id}-subscription-plan-card-details`}
							sx={[reusableStyles.flexCol, subscriptionCardStyles.subscriptionPlanCardDetails]}
						>
							<Box
								id={`${id}-subscription-plan-card-details-wrapper`}
								sx={[
									reusableStyles.flexColWithGap(8),
									subscriptionCardStyles.subscriptionPlanCardDetailsWrapper,
								]}
							>
								<Box id="plan-name" sx={{ alignSelf: 'flex-start' }}>
									<BaseTypography
										id={`${id}-pricing-plan-name`}
										key={`${id}-pricing-plan-name`}
										variant="t1_medium"
										styles={{ color: 'colors.light3', fontSize: '20px !important' }}
									>
										{plan.name}
									</BaseTypography>
								</Box>

								<Box sx={[reusableStyles.flexCol, subscriptionCardStyles.priceContainer]}>
									<Box sx={{ ...reusableStyles.flexCol, alignItems: 'left' }}>
										<Box sx={reusableStyles.flexRowWithGap(4)}>
											<Box sx={reusableStyles.flexRowWithGap(1)}>
												<BaseTypography
													sx={{
														alignSelf: 'flex-start',
														marginBottom: '15px',
														fontSize: '21px !important',
													}}
												>
													$
												</BaseTypography>
												<BaseTypography
													id={`${id}-pricing-plan-value`}
													key={`${id}-pricing-plan-value`}
													variant="d3_bold"
													styles={{
														alignSelf: 'center',
														color: 'colors.light2',
														fontSize: '48px !important',
													}}
												>
													{`${formatPrice(
														planType === SubscriptionPlanType.ANNUALLY
															? plan.monthlyPrice
															: plan.price,
													)}`}
												</BaseTypography>
											</Box>
											<BaseTypography
												variant="t1_v3_medium"
												sx={{
													alignSelf: 'flex-end',
													paddingBottom: '5px',
													fontSize: '21px !important',
												}}
											>
												USD
											</BaseTypography>
											<Box sx={{ alignSelf: 'flex-end' }}>
												{planType === SubscriptionPlanType.ANNUALLY &&
													plansData[plan.name]?.costIcon}
											</Box>
										</Box>
										<Box sx={[reusableStyles.flexRow, { width: '100%', alignItems: 'flex-start' }]}>
											{plan.name !== PricingPagePlans.FREE ? (
												getPerMonthDescription()
											) : (
												<BaseTypography
													id={`${id}-always-free-00`}
													key={`${id}-always-free-00`}
													variant="tooltip_regular"
													styles={{ color: 'colors.light3' }}
												>
													always free
												</BaseTypography>
											)}
										</Box>
									</Box>
									<Box sx={[reusableStyles.flexColWithGap(4), { width: '100%' }]}>
										<Box
											sx={{
												display: 'flex',
												whiteSpace: 'nowrap',
												justifyContent: 'space-between',
											}}
										>
											<BaseTypography
												id={`${id}-credits-validity-text`}
												key={`${id}-credits-validity-text`}
												variant="b1_regular"
												styles={{
													color: 'colors.light3',
													textAlign: 'start',
												}}
											>
												{thousandSeparator(plan.credits)} credits
												{planType !== SubscriptionPlanType.ONE_TIME && `/month`}
											</BaseTypography>
											{plan?.features?.commercialUsage?.restrictions?.allowed === false && (
												<Box
													sx={[
														reusableStyles.flexRow,
														subscriptionCardStyles.nonCommercialUsageText,
													]}
												>
													<BaseTypography
														id={`${id}-button-text`}
														key={`${id}-button-text`}
														variant="tooltip_regular"
														styles={{ color: 'colors.lightCoral' }}
													>
														Non commercial usage
													</BaseTypography>
												</Box>
											)}
											{plan?.features?.creditsRollOver?.restrictions?.allowed && (
												<Box
													sx={[reusableStyles.flexRow, subscriptionCardStyles.nonExpiringTextCont]}
												>
													<BaseTypography
														id={`${id}-button-text`}
														key={`${id}-button-text`}
														variant="tooltip_regular"
														styles={{ color: 'colors.light3' }}
													>
														Credits roll over
													</BaseTypography>
												</Box>
											)}
										</Box>
									</Box>
								</Box>
							</Box>

							<Box
								sx={{
									width: '100%',
									...reusableStyles.flexColWithGap(8),
								}}
							>
								<Button
									id={`${id}-button`}
									sx={[
										subscriptionCardStyles.subscriptionPlanCardSubscribeButton(
											plan?.displaySettings?.highlightLevel === 1,
										),
									]}
									disabled={disableButton || disableButtonOnOngoingAPICall}
									onClick={(e) => {
										e.stopPropagation();
										onButtonClick();
									}}
								>
									<BaseTypography
										id={`${id}-button-text`}
										key={`${id}-button-text`}
										variant="b1_medium"
										styles={{ color: 'inherit' }}
									>
										{buttonText}
									</BaseTypography>
								</Button>

								{isMobile && (
									<BaseButton
										id="see-more-features-button"
										sx={subscriptionCardStyles.seeMoreFeaturesButton}
										onClick={() => handleSeeMoreClick(plan.name)}
									>
										<BaseTypography variant="b2_medium" sx={{ color: 'colors.light3' }}>
											{isMobile &&
											openedMobilePricingPlansDetails &&
											openedMobilePricingPlansDetails.has(plan.name)
												? 'Hide features'
												: 'See more features'}
										</BaseTypography>
									</BaseButton>
								)}
							</Box>
						</Box>
						{isMobile &&
						openedMobilePricingPlansDetails &&
						openedMobilePricingPlansDetails.has(plan.name) ? (
							<SubscriptionCardDetails
								id={id}
								plan={plan}
								planType={planType}
								generationsDetails={generationsDetails}
								offersDetails={offersDetails}
							/>
						) : (
							!isMobile && (
								<SubscriptionCardDetails
									id={id}
									plan={plan}
									planType={planType}
									generationsDetails={generationsDetails}
									offersDetails={offersDetails}
								/>
							)
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
}

export default SubscriptionCard;
